import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Clients from './pages/Clients';
import ClientAccount from './pages/ClientAccount';
import Suppliers from './pages/Suppliers';
import Remitos from './pages/Remitos';
import ServiceAgreements from './pages/ServiceAgreements';
import SupplierAccount from './pages/SupplierAccount';
import Invoices from './pages/Invoices';
import Reports from './pages/Reports';
import EstadoResultados from './pages/EstadoResultados';
import Productions from './pages/Productions';
import ProductionManager from './pages/ProductionManager';
import ProductionAdmin from './pages/ProductionAdmin';
import Samples from './pages/Samples';
import Express from './pages/Express';
import ExpressSamples from './pages/ExpressSamples';
import ExpressStock from './pages/ExpressStock';
import Settings from './pages/Settings';
import Assistant from './pages/Assistant';
import TodoList from './pages/TodoList';
import ProductionTodoList from './pages/ProductionTodoList';
import ContenidoCalendarioV2 from './pages/ContenidoCalendarioV2';
import ContenidoInformes from './pages/ContenidoInformes';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import UserManagement from './pages/UserManagement';
import Colaboradores from './pages/Colaboradores';
import ClientPortalManagement from './pages/ClientPortalManagement';
import ClubProductexConfig from './pages/ClubProductexConfig';
import EmployeePoints from './pages/EmployeePoints';
import EmployeePointsConfig from './pages/EmployeePointsConfig';

// Importaciones del sistema de clientes
import { ClienteAuthProvider } from './context/ClienteAuthContext';
import ClienteLayout from './components/layout/ClienteLayout';
import UnifiedLogin from './pages/UnifiedLogin';
import ClienteDashboard from './pages/cliente/ClienteDashboard';
import ClientePedidos from './pages/cliente/Pedidos';
import ClienteMuestras from './pages/cliente/Muestras';
import ClienteProducciones from './pages/cliente/Producciones';
import ClienteDocumentos from './pages/cliente/Documentos';
import ClientePerfil from './pages/cliente/Perfil';
import ClubProductex from './pages/cliente/ClubProductex';

import Activity from './pages/Activity';
import ActivityClientes from './pages/ActivityClientes';
import Expenses from './pages/Expenses';
import Treasury from './pages/Treasury';
import QuoteInputs from './pages/QuoteInputs';
import Cotizador from './pages/Cotizador';
import RoleManagement from './pages/RoleManagement';
import Pedidos from './pages/Pedidos';
import Logistics from './pages/Logistics';
import Notifications from './pages/Notifications';
import Chat from './pages/Chat';
import Archived from './pages/Archived';
import CrmSeguimiento from './pages/CrmSeguimiento';
import FichasAprobacion from './pages/FichasAprobacion';
import Talleres from './pages/Talleres';
import EmailTemplates from './pages/EmailTemplates';
import WorkshopCalendar from './pages/WorkshopCalendar';
import WorkshopCalendarView from './pages/WorkshopCalendarView';
import TaskBoards from './pages/TaskBoards';
import PublicWorkshopCalendar from './pages/PublicWorkshopCalendar';
import SatisfactionSurvey from './pages/SatisfactionSurvey';
import SatisfactionSurveyAdmin from './pages/SatisfactionSurveyAdmin';
import ClaimForm from './pages/ClaimForm';
import ClaimsAdmin from './pages/ClaimsAdmin';
import ClaimsLinkGenerator from './pages/ClaimsLinkGenerator';
import { useInitialData } from './hooks/useInitialData';
import ErrorModal from './components/modals/ErrorModal';
import LoadingBar from './components/ui/LoadingBar';
import { useAppStore } from './store';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ChatProvider } from './context/ChatContext';
import { ToastProvider, ToastViewport } from './components/ui/Toast';
import { useCrmReminders } from './hooks/useCrmReminders';
import { startEmailQueueProcessor, stopEmailQueueProcessor } from './utils/emailQueueProcessor';

const ReminderScheduler: React.FC = () => {
  // Monta el scheduler una sola vez dentro del árbol con AuthProvider
  console.log('[App] 🚀 ReminderScheduler component mounted');
  useCrmReminders();
  return null;
};

const EmailQueueProcessor: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      console.log('[App] Starting email queue processor...');
      startEmailQueueProcessor();

      return () => {
        stopEmailQueueProcessor();
      };
    }
  }, [user]);

  return null;
};

// Protected route component
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles }) => {
  const { user, userRole, isLoading } = useAuth();
  const location = useLocation();

  // Only save path if user is authenticated and not on auth pages
  useEffect(() => {
    if (user && location.pathname !== '/login' && location.pathname !== '/reset-password') {
      localStorage.setItem('lastPath', location.pathname);
    }
  }, [location.pathname, user]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Save the attempted path before redirecting to login
    if (location.pathname !== '/login' && location.pathname !== '/reset-password') {
      localStorage.setItem('lastPath', location.pathname);
    }
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    // Redirect to appropriate page based on user role
    let redirectPath = '/';
    
    if (userRole === 'Vendedor') {
      redirectPath = '/express';
    } else if (userRole === 'Producción') {
      redirectPath = '/productionmanager';
    } else if (userRole === 'Depósito') {
      redirectPath = '/express/stock';
    } else if (userRole === 'Contenido') {
      redirectPath = '/contenido/calendario';
    }
    
    // Save this redirection
    localStorage.setItem('lastPath', redirectPath);
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  const { isLoading: dataLoading, error: dataError } = useInitialData();
  const { error, showError, clearError, darkMode } = useAppStore();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <>
      <LoadingBar isLoading={dataLoading} />
      
      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<UnifiedLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Public Workshop Calendar - No authentication required */}
        <Route path="/calendario-publico/:token" element={<PublicWorkshopCalendar />} />

        {/* Encuesta de satisfacción - Pública, sin login */}
        <Route path="/satisfaction/:token" element={<SatisfactionSurvey />} />

        {/* Formulario de reclamo - Público, sin login */}
        <Route path="/reclamo/:token" element={<ClaimForm />} />

        {/* Cliente routes - Panel de clientes externos */}
        <Route path="/cliente" element={<ClienteLayout />}>
          <Route index element={<ClienteDocumentos />} />
          <Route path="pedidos" element={<ClientePedidos />} />
          <Route path="muestras" element={<ClienteMuestras />} />
          <Route path="producciones" element={<ClienteProducciones />} />
          <Route path="documentos" element={<ClienteDocumentos />} />
          <Route path="perfil" element={<ClientePerfil />} />
        <Route path="club" element={<ClubProductex />} />
        </Route>

        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          {/* Dashboard - only accessible by admin */}
          <Route index element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="analitica" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Analytics />
            </ProtectedRoute>
          } />
          
          {/* Client routes - accessible by admin, sales, and production */}
          <Route path="clientes" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción']}>
              <Clients />
            </ProtectedRoute>
          } />
          <Route path="clientes/:id" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción']}>
              <ClientAccount />
            </ProtectedRoute>
          } />
          
          {/* Supplier routes - accessible by admin and production */}
          <Route path="proveedores" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <Suppliers />
            </ProtectedRoute>
          } />
          <Route path="proveedores/:id" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <SupplierAccount />
            </ProtectedRoute>
          } />
        
        {/* Invoices route - only accessible by admin */}
        <Route path="facturas" element={
          <ProtectedRoute allowedRoles={['Administrador']}>
            <Invoices />
          </ProtectedRoute>
        } />

        {/* Reports route - only accessible by admin */}
        <Route path="reportes" element={
          <ProtectedRoute allowedRoles={['Administrador']}>
            <Reports />
          </ProtectedRoute>
        } />

        {/* Estado de Resultados - solo Administrador */}
        <Route path="estado-resultados" element={
          <ProtectedRoute allowedRoles={['Administrador']}>
            <EstadoResultados />
          </ProtectedRoute>
        } />
        
        {/* Remitos route - accessible by admin and producción */}
        <Route path="remitos" element={
          <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
            <Remitos />
          </ProtectedRoute>
        } />
        
        {/* Service Agreements route - accessible by admin and vendedor */}
        <Route path="acuerdos-servicio" element={
          <ProtectedRoute allowedRoles={['Administrador', 'Vendedor']}>
            <ServiceAgreements />
          </ProtectedRoute>
        } />
          
          {/* Production routes */}
          <Route path="producciones" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <Productions />
            </ProtectedRoute>
          } />
          <Route path="productionmanager" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <ProductionManager />
            </ProtectedRoute>
          } />
          <Route path="productionadmin" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <ProductionAdmin />
            </ProtectedRoute>
          } />
          
          {/* Sample routes */}
          <Route path="muestras" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <Samples />
            </ProtectedRoute>
          } />

          {/* Fichas Aprobación - PRO */}
          <Route path="fichas" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción']}>
              <FichasAprobacion />
            </ProtectedRoute>
          } />

          {/* Workshop Calendar routes */}
          <Route path="calendario-talleres" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <WorkshopCalendar />
            </ProtectedRoute>
          } />
          <Route path="calendario-talleres/:workshopId" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <WorkshopCalendarView />
            </ProtectedRoute>
          } />
          
          {/* Express routes */}
          <Route path="express" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <Express />
            </ProtectedRoute>
          } />
          <Route path="express/stock" element={
            <ProtectedRoute>
              <ExpressStock />
            </ProtectedRoute>
          } />
          <Route path="express/muestras" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <ExpressSamples />
            </ProtectedRoute>
          } />
          
          {/* Notification routes - Rutas para navegación desde notificaciones */}
          <Route path="productions" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <Productions />
            </ProtectedRoute>
          } />
          <Route path="samples" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <Samples />
            </ProtectedRoute>
          } />
          <Route path="expresssamples" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <ExpressSamples />
            </ProtectedRoute>
          } />
          
          {/* Chat route */}
          <Route path="chat" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor', 'Producción', 'Depósito']}>
              <Chat />
            </ProtectedRoute>
          } />
          
          {/* Pedidos route */}
          <Route path="pedidos" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Depósito']}>
              <Pedidos />
            </ProtectedRoute>
          } />
          

          
          {/* Activity route */}
          <Route path="actividad" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Activity />
            </ProtectedRoute>
          } />
          <Route path="actividad-clientes" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <ActivityClientes />
            </ProtectedRoute>
          } />

          {/* Tableros de gestión - solo Administrador */}
          <Route path="tableros" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <TaskBoards />
            </ProtectedRoute>
          } />
          
          {/* Expenses route */}
          <Route path="gastos" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <Expenses />
            </ProtectedRoute>
          } />
          
          {/* Treasury route */}
          <Route path="tesoreria" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Treasury />
            </ProtectedRoute>
          } />
          
         {/* Logistics route */}
         <Route path="/logistics" element={
           <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
             <Logistics />
           </ProtectedRoute>
         } />
         
          {/* Quote Inputs route */}
          <Route path="configuracion/cotizador" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <QuoteInputs />
            </ProtectedRoute>
          } />
          
          {/* Role Management route */}
          <Route path="roles-permisos" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <RoleManagement />
            </ProtectedRoute>
          } />
          
          {/* Cotizador route */}
          <Route path="cotizador" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor']}>
              <Cotizador />
            </ProtectedRoute>
          } />
          
          {/* Settings and other routes */}
          <Route path="configuracion" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="asistente" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Assistant />
            </ProtectedRoute>
          } />
          <Route path="todolist" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <TodoList />
            </ProtectedRoute>
          } />

          <Route path="contenido" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Contenido']}>
              <Navigate to="/contenido/calendario" replace />
            </ProtectedRoute>
          } />

          <Route path="contenido/calendario" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Contenido']}>
              <ContenidoCalendarioV2 />
            </ProtectedRoute>
          } />

          <Route path="contenido/informes" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Contenido']}>
              <ContenidoInformes />
            </ProtectedRoute>
          } />

          <Route path="production-todos" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <ProductionTodoList />
            </ProtectedRoute>
          } />
          
          {/* Talleres route */}
          <Route path="talleres" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Producción']}>
              <Talleres />
            </ProtectedRoute>
          } />
          
          {/* User management - admin only */}
          <Route path="usuarios" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <UserManagement />
            </ProtectedRoute>
          } />

          {/* Colaboradores / RRHH - admin only */}
          <Route path="colaboradores" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Colaboradores />
            </ProtectedRoute>
          } />
          
          {/* Client Portal Management - admin only */}
          <Route path="portales-clientes" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <ClientPortalManagement />
            </ProtectedRoute>
          } />
          
          {/* Club Productex® Configuration - admin only */}
          <Route path="club-productex-config" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <ClubProductexConfig />
            </ProtectedRoute>
          } />

          {/* Puntos colaboradores - todos los roles */}
          <Route path="puntos-colaboradores" element={
            <ProtectedRoute>
              <EmployeePoints />
            </ProtectedRoute>
          } />

          {/* Puntos colaboradores - configuración admin */}
          <Route path="puntos-colaboradores-config" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Super Admin']}>
              <EmployeePointsConfig />
            </ProtectedRoute>
          } />
          
          {/* Notifications route */}
          <Route path="notifications" element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } />

          {/* Email Templates - admin only */}
          <Route path="email-templates" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <EmailTemplates />
            </ProtectedRoute>
          } />

          {/* Archived - admin only */}
          <Route path="archivados" element={
            <ProtectedRoute allowedRoles={['Administrador']}>
              <Archived />
            </ProtectedRoute>
          } />

          {/* Encuestas de satisfacción - admin only */}
          <Route path="encuestas-satisfaccion" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Super Admin']}>
              <SatisfactionSurveyAdmin />
            </ProtectedRoute>
          } />

          {/* Reclamos - admin: ver y editar; vendedores: generar link */}
          <Route path="reclamos" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Super Admin']}>
              <ClaimsAdmin />
            </ProtectedRoute>
          } />
          <Route path="generar-reclamo" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor']}>
              <ClaimsLinkGenerator />
            </ProtectedRoute>
          } />

          {/* CRM Seguimiento - admin and vendedor */}
          <Route path="seguimiento" element={
            <ProtectedRoute allowedRoles={['Administrador', 'Vendedor']}>
              <CrmSeguimiento />
            </ProtectedRoute>
          } />

        </Route>
      </Routes>

      {dataError && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                <svg className="h-6 w-6 text-red-600 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900 dark:text-white">Error al cargar datos</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {dataError}
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-900"
                  onClick={() => window.location.reload()}
                >
                  Reintentar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={showError}
        onClose={clearError}
        message={error || 'Ha ocurrido un error inesperado'}
      />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <NotificationProvider>
            <ClienteAuthProvider>
              <ChatProvider>
                <ReminderScheduler />
                <EmailQueueProcessor />
                <AppContent />
              </ChatProvider>
            </ClienteAuthProvider>
          </NotificationProvider>
        </AuthProvider>
        <ToastViewport />
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;